import * as React from 'react';

import { getApiBaseUrl } from '../lib/getEnv';

// ─── Request types ───────────────────────────────────────────────────────────

interface ICheckEmailRequest {
  email: string;
}

interface IRegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

interface IVerifyOtpRequest {
  userId: string;
  code: string;
}

interface IResendOtpRequest {
  userId: string;
  channel?: 'sms' | 'email';
}

interface ILoginRequest {
  email: string;
  password: string;
}

interface IForgotPasswordRequest {
  email: string;
}

interface IResetPasswordRequest {
  email: string;
  emailCode: string;
  smsCode?: string;
  newPassword: string;
}

interface ISendPasswordResetSmsRequest {
  email: string;
  code: string;
}

interface IForgotPasswordResponse {
  success: true;
  requiresSmsOtp: boolean;
  phoneLast4?: string;
}

// ─── Response types ──────────────────────────────────────────────────────────

interface ICheckEmailAvailable {
  status: 'AVAILABLE';
}

interface ICheckEmailExists {
  status: 'EXISTS';
}

interface ICheckEmailPending {
  status: 'PENDING_VERIFICATION';
}

interface ICheckEmailGoogle {
  provider: 'GOOGLE';
  email: string;
}

type CheckEmailResponse = ICheckEmailAvailable | ICheckEmailExists | ICheckEmailPending | ICheckEmailGoogle;

interface IRegisterResponse {
  userId: string;
}

type OtpChannel = 'sms' | 'email';

interface ILoginResponse {
  requiresOtp: boolean;
  userId: string;
  otpChannel?: OtpChannel;
  phoneLast4?: string;
}

interface IResendLoginOtpResponse {
  success: true;
  otpChannel: OtpChannel;
  phoneLast4?: string;
}

interface ISuccessResponse {
  success: true;
}

interface ISendPasswordResetSmsResponse {
  success: true;
  phoneLast4?: string;
}

// ─── Error type ──────────────────────────────────────────────────────────────

interface IAuthApiError {
  error: string;
  code?: string;
}

// ─── Result type ─────────────────────────────────────────────────────────────

type ApiResult<T> = { ok: true; data: T } | { ok: false; error: IAuthApiError; status: number };

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Typed wrapper around `fetch` for all `/auth/*` REST endpoints.
 *
 * Each method returns an `ApiResult<T>` discriminated union — callers check
 * `result.ok` to narrow to success or error without try/catch.
 *
 * @returns Object with methods for each auth endpoint (checkEmail, register,
 *   login, verifyRegistrationOtp, etc.). All requests include credentials
 *   for cookie-based sessions.
 */
const useAuthApi = () => {
  const baseUrl = React.useMemo(() => getApiBaseUrl(), []);

  const request = React.useCallback(
    async <T>(path: string, body: object): Promise<ApiResult<T>> => {
      try {
        const response = await fetch(`${baseUrl}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });

        const data: unknown = await response.json();

        if (!response.ok) {
          return { ok: false, error: data as IAuthApiError, status: response.status };
        }

        return { ok: true, data: data as T };
      } catch {
        return {
          ok: false,
          error: { error: 'Something went wrong. Please try again.' },
          status: 0,
        };
      }
    },
    [baseUrl]
  );

  return React.useMemo(
    () => ({
      checkEmail: (body: ICheckEmailRequest) => request<CheckEmailResponse>('/auth/check-email', body),
      register: (body: IRegisterRequest) => request<IRegisterResponse>('/auth/register', body),
      verifyRegistrationOtp: (body: IVerifyOtpRequest) =>
        request<ISuccessResponse>('/auth/verify-registration-otp', body),
      resendRegistrationOtp: (body: IResendOtpRequest) =>
        request<ISuccessResponse>('/auth/resend-registration-otp', body),
      login: (body: ILoginRequest) => request<ILoginResponse>('/auth/login', body),
      verifyLoginOtp: (body: IVerifyOtpRequest) => request<ISuccessResponse>('/auth/verify-login-otp', body),
      resendLoginOtp: (body: IResendOtpRequest) => request<IResendLoginOtpResponse>('/auth/resend-login-otp', body),
      forgotPassword: (body: IForgotPasswordRequest) => request<IForgotPasswordResponse>('/auth/forgot-password', body),
      sendPasswordResetSms: (body: ISendPasswordResetSmsRequest) =>
        request<ISendPasswordResetSmsResponse>('/auth/send-password-reset-sms', body),
      resetPassword: (body: IResetPasswordRequest) => request<ISuccessResponse>('/auth/reset-password', body),
    }),
    [request]
  );
};

export default useAuthApi;

export type {
  ApiResult,
  CheckEmailResponse,
  IAuthApiError,
  ICheckEmailAvailable,
  ICheckEmailExists,
  ICheckEmailGoogle,
  ICheckEmailPending,
  IForgotPasswordRequest,
  ILoginRequest,
  ILoginResponse,
  IRegisterRequest,
  IRegisterResponse,
  IResetPasswordRequest,
  ISuccessResponse,
  OtpChannel,
};
